import { ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Alert, Stack, Typography } from "@mui/material";
import { FormikProps } from "formik";

import { FormikTextField } from "../../../../../../formik/FormikTextField";
import { FormikYesNoAutocomplete } from "../../../../../../formik/FormikYesNoAutocomplete";
import { nameof } from "../../../../../../utils/nameof";
import { AssetExpenseSummaryResponse, AssetMaintenanceExpenseResponse } from "../../../../../api/ClientFileApi";
import { IncomeSectionCard } from "../../../income/components/income-section-card/IncomeSectionCard";
import { FieldWithInfo } from "../../../personal-data/components/field-with-info/FieldWithInfo";
import { AssetExpensesFormValues, ExpensesMaxLength } from "../../ExpensesView.types";
import { computeTotalExpenses, switchCostSharing } from "../../ExpensesView.utils";
import { ExpenseSummary } from "./components/expense-summary/ExpenseSummary";
import { ExpenseTable } from "./components/expense-table/ExpenseTable";

const SECTION = "clients.client.debtSettlement.assetInventory.expensesTab.obligations";

interface Props {
  formik: FormikProps<AssetExpensesFormValues>;
  maintenance: AssetMaintenanceExpenseResponse;
  summary: AssetExpenseSummaryResponse | undefined;
  saveAttempted: boolean;
  livingCostsBelowMinimum: boolean;
  partnerBlock?: ReactNode;
}

export function RecurringObligationsSection({
  formik,
  maintenance,
  summary,
  saveAttempted,
  livingCostsBelowMinimum,
  partnerBlock,
}: Props) {
  const { t } = useTranslation();
  const { paysCostsAlone } = formik.values;
  const isAnswered = paysCostsAlone !== null;
  const questionLabel = t(`${SECTION}.paysCostsAlone`);
  const remarksLabel = t(`${SECTION}.remarks`);
  const totalExpenses = computeTotalExpenses(formik.values.positions, maintenance.amount);

  const onCostSharingChange = (next: boolean | null) => {
    if (next !== null && paysCostsAlone !== null && next !== paysCostsAlone) {
      void formik.setFieldValue(
        nameof<AssetExpensesFormValues>("positions"),
        switchCostSharing(formik.values.positions, next)
      );
    }
  };

  return (
    <IncomeSectionCard title={t(`${SECTION}.title`)}>
      <Stack gap={1}>
        <FieldWithInfo fieldLabel={questionLabel} info={t(`${SECTION}.paysCostsAloneInfo`)}>
          <FormikYesNoAutocomplete
            id="expenses-pays-costs-alone"
            formik={formik}
            fieldName="paysCostsAlone"
            label={questionLabel}
            onValueChange={onCostSharingChange}
          />
        </FieldWithInfo>
        {paysCostsAlone === false && (
          <FormikTextField
            formik={formik}
            fieldName="costSharingExplanation"
            label={t(`${SECTION}.costSharingExplanation`)}
            multiline
            minRows={3}
            slotProps={{ input: { inputProps: { maxLength: ExpensesMaxLength.costSharingExplanation } } }}
          />
        )}
      </Stack>
      {isAnswered && (
        <>
          <Alert severity="info">
            <Stack gap={1}>
              <Typography variant="body2">
                <Trans i18nKey={`${SECTION}.hintMonthlyAmounts`} components={{ bold: <strong /> }} />
              </Typography>
              <Typography variant="body2">{t(`${SECTION}.hintProofs`)}</Typography>
              <Typography variant="body2">
                <strong>{t(`${SECTION}.hintNoProofsTitle`)}</strong> {t(`${SECTION}.hintNoProofsItems`)}
              </Typography>
            </Stack>
          </Alert>
          <ExpenseTable
            formik={formik}
            maintenance={maintenance}
            totalExpenses={totalExpenses}
            livingCostsBelowMinimum={livingCostsBelowMinimum}
          />
          <FieldWithInfo fieldLabel={remarksLabel} info={t(`${SECTION}.remarksInfo`)}>
            <FormikTextField
              formik={formik}
              fieldName="expenseRemarks"
              label={remarksLabel}
              multiline
              minRows={3}
              slotProps={{ input: { inputProps: { maxLength: ExpensesMaxLength.expenseRemarks } } }}
            />
          </FieldWithInfo>
          {summary && <ExpenseSummary summary={summary} totalExpenses={totalExpenses} saveAttempted={saveAttempted} />}
          {!paysCostsAlone && partnerBlock}
        </>
      )}
    </IncomeSectionCard>
  );
}
